import { setPageDescription } from "../seo";
import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FiSend, FiUser, FiMail, FiPhone, FiBriefcase, FiPackage, FiMessageSquare } from "react-icons/fi";
import Header from "../components/Header";
import Footer from "../components/Footer";
import HeroFX from "../components/HeroFX";
import PageLoader from "../components/PageLoader";
import { Field, FormAlert, FormCard, SubmitButton, SuccessPanel } from "../components/forms/formKit";
import { fetchSystem, fetchSystemPage, fetchVisitorCountry, submitQuoteRequest } from "../api";
import { fillEmail, formatPrice, pickCurrency, priceFor, quotedPrice } from "../systemPageUtils";

const SECTION = "scroll-mt-28";
const TAG = "inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-light/10 border border-accent-light/30 text-accent-light text-[11px] font-bold tracking-widest uppercase";

function Counter({ end, suffix = "" }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") { setCount(end); return undefined; }
    let timer;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      let value = 0;
      const step = Math.max(1, end / 50);
      timer = setInterval(() => {
        value += step;
        if (value >= end) { setCount(end); clearInterval(timer); } else setCount(Math.floor(value));
      }, 20);
    }, { threshold: 0.4 });
    if (ref.current) observer.observe(ref.current);
    return () => { observer.disconnect(); clearInterval(timer); };
  }, [end]);
  return <span ref={ref}>{count}{suffix}</span>;
}

function SectionHead({ tag, title, subtitle }) {
  if (!tag && !title && !subtitle) return null;
  return (
    <div className="text-center mb-12">
      {tag && <span className={TAG}>&#10022; {tag}</span>}
      {title && <h2 className="mt-4 text-3xl md:text-4xl font-extrabold tracking-tight">{title}</h2>}
      {subtitle && <p className="mt-3 text-gray-400 max-w-2xl mx-auto">{subtitle}</p>}
    </div>
  );
}

// The card in the hero that turns through the modules, one every three seconds.
function ModuleTicker({ modules }) {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (modules.length < 2) return undefined;
    const timer = setInterval(() => setIndex((i) => (i + 1) % modules.length), 3000);
    return () => clearInterval(timer);
  }, [modules.length]);
  if (!modules.length) return null;
  const mod = modules[index % modules.length];
  return (
    <div className="mt-12 max-w-2xl mx-auto rounded-2xl bg-navy/70 border border-white/10 backdrop-blur p-6 text-left relative overflow-hidden" style={{ borderColor: `${mod.color}55` }}>
      <div className="absolute top-0 inset-x-0 h-[3px] bg-brand-gradient" />
      <AnimatePresence mode="wait">
        <motion.div key={mod.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}>
          <div className="flex items-center gap-3">
            <span className="text-3xl">{mod.icon}</span>
            <h3 className="text-xl font-bold" style={{ color: mod.color }}>{mod.title}</h3>
          </div>
          <p className="mt-3 text-gray-300 text-sm leading-relaxed">{mod.description}</p>
        </motion.div>
      </AnimatePresence>
      <div className="mt-5 flex justify-center gap-1.5">
        {modules.map((m, i) => (
          <button key={m.id} type="button" aria-label={`Show ${m.title}`} onClick={() => setIndex(i)}
            className={`h-1 rounded-full transition-all ${i === index ? "w-8 bg-accent-light" : "w-5 bg-white/20 hover:bg-white/40"}`} />
        ))}
      </div>
    </div>
  );
}

function FeatureTabs({ features }) {
  const [active, setActive] = useState(0);
  const [broken, setBroken] = useState({});
  const feature = features[active % features.length];
  const showImage = feature.image && !broken[feature.id];
  const anyScreenshot = features.some((f) => f.image);          // a system with no screenshots yet shows the text on its own, not an empty frame
  return (
    <div className="grid lg:grid-cols-2 gap-10 items-center">
      <div className="flex flex-col gap-1" role="tablist">
        {features.map((f, i) => (
          <button key={f.id} type="button" role="tab" aria-selected={i === active} onClick={() => setActive(i)}
            className={`text-left flex items-start gap-4 p-4 rounded-xl border transition-colors ${i === active ? "bg-accent-light/10 border-accent-light/40" : "border-transparent hover:bg-white/5"}`}>
            <span className="text-2xl shrink-0">{f.icon}</span>
            <span>
              <span className="block font-semibold">{f.title}</span>
              {i === active && <span className="block mt-1 text-sm text-gray-400 leading-relaxed">{f.description}</span>}
            </span>
          </button>
        ))}
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={feature.id} initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}
          className="rounded-2xl overflow-hidden border border-accent-light/30 bg-navy-dark shadow-[0_0_30px_rgba(34,211,238,0.12)]">
          {/* The whole screenshot, at its own proportions - never cropped, and nothing is laid over it. */}
          {showImage ? (
            <img src={feature.image} alt={feature.title} className="block w-full h-auto" onError={() => setBroken((b) => ({ ...b, [feature.id]: true }))} />
          ) : anyScreenshot ? (
            <div className="w-full aspect-[4/3] flex flex-col items-center justify-center gap-3 text-gray-500">
              <span className="text-6xl">{feature.icon}</span>
              <span className="text-sm">Screenshot coming soon</span>
            </div>
          ) : (
            <div className="px-8 py-12 text-center">
              <span className="text-6xl">{feature.icon}</span>
              <h3 className="mt-5 text-xl font-bold">{feature.title}</h3>
              <p className="mt-3 text-gray-300 leading-relaxed">{feature.description}</p>
            </div>
          )}
          {(showImage || anyScreenshot) && (
            <div className="border-t border-white/10 p-4">
              <h3 className="text-sm font-bold">{feature.title}</h3>
              <p className="text-xs text-gray-400 mt-1 leading-relaxed">{feature.description}</p>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function Pricing({ plans, currencies, currency, setCurrency, texts, onQuote }) {
  return (
    <>
      {currencies.length > 1 && (
        <div className="flex items-center justify-center gap-3 mb-10 -mt-4">
          <label htmlFor="sys-currency" className="text-sm text-gray-400">{texts.currency_label || "Prices in"}</label>
          <select id="sys-currency" className="fx-input !w-auto !py-1.5 text-sm" value={currency ? currency.country_code : ""}
            onChange={(e) => setCurrency(currencies.find((c) => c.country_code === e.target.value))}>
            {currencies.map((c) => (
              <option key={c.country_code} value={c.country_code}>{c.currency_code} ({c.symbol}){c.name ? ` - ${c.name}` : ""}</option>
            ))}
          </select>
        </div>
      )}
      <div className="grid md:grid-cols-3 gap-6 items-start">
        {plans.map((plan) => {
          const price = currency ? priceFor(plan, currency.country_code) : null;
          return (
            <div key={plan.id} className={`relative p-7 rounded-2xl border flex flex-col ${plan.is_popular ? "bg-accent-light/5 border-accent-light shadow-lg shadow-accent/20 md:scale-[1.03]" : "bg-white/5 border-white/10"}`}>
              {plan.is_popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-brand-gradient text-xs font-semibold whitespace-nowrap">&#9733; {texts.popular_badge_text || "MOST POPULAR"}</span>}
              <h3 className="text-sm font-bold tracking-widest uppercase text-accent-light">{plan.name}</h3>
              <div className="mt-3 text-4xl font-extrabold">{price != null ? formatPrice(currency.symbol, price) : "Ask us"}</div>
              {price != null && currency && <div className="text-xs text-gray-400 mt-1">{plan.period || texts.price_period}{plan.period || texts.price_period ? " · " : ""}{currency.currency_code}</div>}
              <ul className="mt-5 space-y-2 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="text-sm text-gray-300 flex gap-2"><span className="text-accent-light">&#10003;</span> {f}</li>
                ))}
              </ul>
              <button type="button" onClick={() => onQuote(plan.name)}
                className={`mt-6 w-full px-5 py-2.5 rounded-full font-semibold text-sm transition ${plan.is_popular ? "bg-brand-gradient text-white" : "border border-navy-border hover:border-accent-light/60"}`}>
                {(texts.plan_button_text || "Get {plan} Quote \u2192").replace(/\{plan\}/g, plan.name)}
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
}

const EMPTY_FORM = { name: "", email: "", company: "", phone: "", package_name: "", message: "" };

function QuoteSection({ data, systemHeading, plans, currency, packageName, setPackageName }) {
  const { texts } = data;
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState("idle");           // idle | sending | sent
  const [error, setError] = useState("");
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const chosen = packageName || (plans.find((p) => p.is_popular) || plans[0] || {}).name || "";

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    const plan = plans.find((p) => p.name === chosen);
    try {
      await submitQuoteRequest({
        ...form,
        package_name: chosen,
        service: chosen ? `${systemHeading} - ${chosen}` : systemHeading,
        quoted_price: plan && currency ? quotedPrice(currency, priceFor(plan, currency.country_code)) : "",
      });
      setStatus("sent");
    } catch {
      setStatus("idle");
      setError("Something went wrong. Please try again.");
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-12 items-start">
      <div>
        {texts.quote_tag && <span className={TAG}>&#10022; {texts.quote_tag}</span>}
        {texts.quote_title && <h2 className="mt-4 text-3xl md:text-4xl font-extrabold tracking-tight">{texts.quote_title}</h2>}
        {texts.quote_text && <p className="mt-4 text-gray-300 leading-relaxed">{texts.quote_text}</p>}
        <div className="mt-8 space-y-5">
          {data.quote_points.map((p) => (
            <div key={p.title} className="flex items-start gap-4">
              <span className="w-10 h-10 shrink-0 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-lg">{p.icon}</span>
              <div><div className="font-semibold text-sm">{p.title}</div><div className="text-sm text-gray-400">{p.description}</div></div>
            </div>
          ))}
        </div>
        {data.trust_points.length > 0 && (
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-400">
            {data.trust_points.map((t) => <span key={t.text}>{t.icon} {t.text}</span>)}
          </div>
        )}
      </div>

      <FormCard title={texts.quote_form_title || "Request a quote"} icon={FiSend}>
        {status === "sent" ? (
          <SuccessPanel title={texts.quote_success_title || "Request sent!"} action={texts.quote_again_text || "Request another quote"} onAction={() => { setStatus("idle"); setForm(EMPTY_FORM); }}>
            {fillEmail(texts.quote_success_text, form.email) || "Thank you! We will contact you shortly."}
          </SuccessPanel>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full name" required icon={FiUser} placeholder="John Smith" autoComplete="name" value={form.name} onChange={set("name")} />
              <Field label="Email address" required icon={FiMail} type="email" placeholder="john@company.com" autoComplete="email" value={form.email} onChange={set("email")} />
              <Field label="Company name" icon={FiBriefcase} placeholder="Your Company LLC" autoComplete="organization" value={form.company} onChange={set("company")} />
              <Field label="Phone number" icon={FiPhone} placeholder="+92 300 1234567" autoComplete="tel" value={form.phone} onChange={set("phone")} />
            </div>
            {plans.length > 0 && (
              <Field as="select" label="Package interest" icon={FiPackage} value={chosen} onChange={(e) => setPackageName(e.target.value)}>
                {plans.map((p) => {
                  const price = currency ? priceFor(p, currency.country_code) : null;
                  return <option key={p.id} value={p.name}>{p.name}{price != null ? ` - ${formatPrice(currency.symbol, price)}` : ""}</option>;
                })}
                <option value="Custom">{texts.quote_custom_option || "Custom - I need something specific"}</option>
              </Field>
            )}
            <Field as="textarea" label="Your requirements" icon={FiMessageSquare} rows={4} placeholder="Tell us about your business, number of employees, the modules you need, or any questions..." value={form.message} onChange={set("message")} />
            <FormAlert>{error}</FormAlert>
            <SubmitButton busy={status === "sending"} icon={FiSend} className="w-full">{texts.quote_button_text || "Send"}</SubmitButton>
          </form>
        )}
      </FormCard>
    </div>
  );
}

function scrollToSection(target) {
  const el = document.getElementById(`sys-${target}`);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function SystemPageBody({ data, country }) {
  const { texts, system } = data;
  // Prices start in the currency of the visitor's country (from their IP address; ?country=PK shows another country's), and they can change it.
  const [currency, setCurrency] = useState(() => pickCurrency(data.currencies, typeof navigator !== "undefined" ? (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language]) : [], country));
  const [packageName, setPackageName] = useState("");
  const jumpToQuote = (plan) => { setPackageName(plan); scrollToSection("quote"); };

  return (
    <>
      <section id="sys-hero" className="relative text-white bg-navy overflow-hidden border-b border-navy-border">
        <HeroFX />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12 pt-14 pb-16 text-center">
          <Link to="/#systems" className="inline-block text-accent-light text-sm mb-6 hover:underline">&larr; Back to Systems</Link>
          <div>
            {texts.hero_badge && <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={TAG}>{texts.hero_badge}</motion.span>}
          </div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mt-6 text-4xl md:text-6xl font-extrabold tracking-tight leading-[1.05]">
            <span className="block">{texts.title_line1 || system.heading}</span>
            {texts.title_line2 && <span className="block bg-brand-gradient bg-clip-text text-transparent">{texts.title_line2}</span>}
          </motion.h1>
          {texts.subtitle && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }} className="mt-6 text-gray-300 text-lg max-w-2xl mx-auto leading-relaxed">{texts.subtitle}</motion.p>}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="mt-8 flex flex-wrap items-center justify-center gap-3">
            {texts.cta_primary_text && <button type="button" onClick={() => scrollToSection("quote")} className="px-7 py-3 rounded-full bg-brand-gradient text-white font-semibold shadow-lg shadow-accent/20">{texts.cta_primary_text}</button>}
            {texts.cta_secondary_text && texts.login_url && <a href={texts.login_url} target="_blank" rel="noopener noreferrer" className="px-7 py-3 rounded-full border border-navy-border font-semibold hover:border-accent-light/60 transition">{texts.cta_secondary_text}</a>}
          </motion.div>
          <ModuleTicker modules={data.modules} />
        </div>
      </section>

      {data.nav_links.length > 0 && (
        <nav aria-label="On this page" className="sticky top-16 z-30 bg-navy/90 backdrop-blur border-b border-navy-border">
          <div className="max-w-5xl mx-auto px-6 flex items-center justify-center gap-1 overflow-x-auto">
            {data.nav_links.map((l) => (
              <button key={l.target} type="button" onClick={() => scrollToSection(l.target)} className="px-4 py-3 text-sm font-semibold text-gray-300 hover:text-accent-light whitespace-nowrap">{l.label}</button>
            ))}
          </div>
        </nav>
      )}

      {data.stats.length > 0 && (
        <div className="bg-brand-gradient text-white">
          <div className="max-w-5xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-y-6">
            {data.stats.map((s) => (
              <div key={s.label} className="text-center px-3">
                <div className="text-4xl md:text-5xl font-extrabold"><Counter end={s.value} suffix={s.suffix} /></div>
                <div className="mt-2 text-[11px] tracking-widest uppercase opacity-80">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.features.length > 0 && (
        <section id="sys-features" className={`${SECTION} max-w-7xl mx-auto px-6 md:px-12 py-20`}>
          <SectionHead tag={texts.features_tag} title={texts.features_title} subtitle={texts.features_subtitle} />
          <FeatureTabs features={data.features} />
        </section>
      )}

      {data.modules.length > 0 && (
        <section id="sys-modules" className={`${SECTION} bg-navy-dark border-y border-navy-border`}>
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
            <SectionHead tag={texts.modules_tag} title={texts.modules_title} />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {data.modules.map((m) => (
                <div key={m.id} className="p-6 rounded-2xl bg-white/5 border border-white/10">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl mb-4" style={{ background: `${m.color}22` }}>{m.icon}</div>
                  <h3 className="text-lg font-bold" style={{ color: m.color }}>{m.title}</h3>
                  <p className="text-sm text-gray-400 mt-2 leading-relaxed">{m.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {data.plans.length > 0 && (
        <section id="sys-pricing" className={`${SECTION} max-w-7xl mx-auto px-6 md:px-12 py-20`}>
          <SectionHead tag={texts.pricing_tag} title={texts.pricing_title} subtitle={texts.pricing_subtitle} />
          <Pricing plans={data.plans} currencies={data.currencies} currency={currency} setCurrency={setCurrency} texts={texts} onQuote={jumpToQuote} />
        </section>
      )}

      <section id="sys-quote" className={`${SECTION} bg-navy-dark border-t border-navy-border`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
          <QuoteSection data={data} systemHeading={system.heading} plans={data.plans} currency={currency} packageName={packageName} setPackageName={setPackageName} />
        </div>
      </section>
    </>
  );
}

// A system that has no detail content yet: its own heading and text with a button to ask for a quote.
function PlainSystem({ system }) {
  return (
    <section className="relative text-white bg-navy overflow-hidden">
      <HeroFX />
      <div className="relative max-w-3xl mx-auto px-6 py-24 text-center">
        <Link to="/#systems" className="inline-block text-accent-light text-sm mb-6 hover:underline">&larr; Back to Systems</Link>
        <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight">{system.heading}</h1>
        {system.paragraph1 && <div className="mt-6 text-gray-300 text-lg leading-relaxed [&_p]:mb-3" dangerouslySetInnerHTML={{ __html: system.paragraph1 }} />}
        <Link to="/contact" className="inline-block mt-8 px-7 py-3 rounded-full bg-brand-gradient font-semibold">Contact us</Link>
      </div>
    </section>
  );
}

export default function SystemDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);       // the page content, or false when the system has none
  const [country, setCountry] = useState("");
  const [system, setSystem] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setSystem(null);
    setNotFound(false);
    fetchSystem(id)
      .then((s) => {
        setSystem(s);
        const asked = new URLSearchParams(window.location.search).get("country");
        return Promise.all([fetchSystemPage(id).catch(() => false), asked ? Promise.resolve(asked) : fetchVisitorCountry()])
          .then(([page, code]) => { setCountry(code || ""); setData(page); });
      })
      .catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => {
    if (!system) return;
    document.title = `${system.heading} - Mr. Aali Online Technologies`;
    setPageDescription((data && data.texts.subtitle) || system.label || system.heading);
  }, [system, data]);

  if (notFound) {
    return (
      <>
        <Header />
        <div className="h-16" />
        <div className="min-h-[60vh] flex flex-col items-center justify-center text-white bg-navy text-center px-6">
          <h1 className="text-3xl font-bold mb-4">System not found</h1>
          <Link to="/#systems" className="text-accent-light hover:underline">Back to Systems</Link>
        </div>
        <Footer />
      </>
    );
  }
  if (!system || data === null) return <PageLoader />;

  return (
    <>
      <Header />
      <div className="h-16" />
      <div className="text-white bg-navy">
        {data ? <SystemPageBody data={data} country={country} /> : <PlainSystem system={system} />}
      </div>
      <Footer />
    </>
  );
}
